import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "./ui-common/button";
import { Card } from "./ui-common/card";
import { Badge } from "./ui-common/badge";
import { Input } from "./ui-common/input";
import { ScrollArea } from "./ui-common/scroll-area";
import { Switch } from "./ui-common/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui-common/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui-common/select";
import { Checkbox } from "./ui-common/checkbox";
import { Label } from "./ui-common/label";

import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Search,
  Filter,
  LogOut,
  LifeBuoy,
  Copy,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Eye
} from "lucide-react";
import axiosInstance from "./axiosInstance";
import { formatDate } from "../lib/utils";
import { SourceProvider } from "../contexts/SourceContext";
import { ChatResponse, SearchResponse } from "./ui-chat/response";

const AdminChatViewer = ({
  user,
  onBack,
  onLogout,
  onNavigateToSupport
}) => {
  const navigate = useNavigate();

  // State for conversations and messages
  const [allConversations, setAllConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [currentQueryUuid, setCurrentQueryUuid] = useState(null);
  const [currentMessages, setCurrentMessages] = useState([]);

  // State for courses list
  const [allCourses, setAllCourses] = useState([]);

  // Filter states
  const [inputMessageUuid, setInputMessageUuid] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [upvoteFilter, setUpvoteFilter] = useState(false);
  const [downvoteFilter, setDownvoteFilter] = useState(false);

  // UI states
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Current index for navigation
  const currentIndex = filteredConversations.findIndex(
    (c) => c.query_uuid === currentQueryUuid
  );

  // Fetch conversations with filters
  const fetchConversations = async () => {
    setIsLoading(true);
    const params = {};
    if (inputMessageUuid) params.message_uuid = inputMessageUuid;
    if (courseFilter) params.course = courseFilter;
    if (startTime) params.start_time = new Date(startTime).toISOString();
    if (endTime) params.end_time = new Date(endTime).toISOString();
    if (upvoteFilter) params.upvote = 1;
    if (downvoteFilter) params.downvote = 1;

    try {
      const response = await axiosInstance.get("/api/admin/conversations", {
        timeout: 180000,
        params
      });
      setFilteredConversations(response.data);
      if (response.data.length) {
        setCurrentQueryUuid(response.data[0].query_uuid);
      } else {
        setCurrentQueryUuid(null);
        setCurrentMessages([]);
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Initial load - fetch all conversations to get courses list
  useEffect(() => {
    const initializeData = async () => {
      try {
        const response = await axiosInstance.get("/api/admin/conversations", {
          timeout: 180000
        });
        setAllConversations(response.data);

        // Extract unique courses
        const courses = Array.from(
          new Set(response.data.map((c) => c.conversation_course).filter(Boolean))
        );
        setAllCourses(courses);

        // Set initial filtered data
        setFilteredConversations(response.data);
        if (response.data.length) {
          setCurrentQueryUuid(response.data[0].query_uuid);
        }
      } catch (error) {
        console.error("Failed to initialize admin viewer:", error);
        // Check if it's a 403 Forbidden error (not an admin)
        if (error.response?.status === 403) {
          setAccessDenied(true);
        }
      } finally {
        setIsInitialLoading(false);
      }
    };

    initializeData();
  }, []);

  // Update messages when selection or data changes
  useEffect(() => {
    const convo = filteredConversations.find(
      (c) => c.query_uuid === currentQueryUuid
    );
    setCurrentMessages(convo ? convo.messages : []);
  }, [currentQueryUuid, filteredConversations]);

  const currentConversation = filteredConversations.find(
    (c) => c.query_uuid === currentQueryUuid
  ) || null;

  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevId = filteredConversations[currentIndex - 1]?.query_uuid;
      if (prevId) setCurrentQueryUuid(prevId);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredConversations.length - 1) {
      const nextId = filteredConversations[currentIndex + 1]?.query_uuid;
      if (nextId) setCurrentQueryUuid(nextId);
    }
  };

  const clearFilters = () => {
    setInputMessageUuid("");
    setCourseFilter("");
    setStartTime("");
    setEndTime("");
    setUpvoteFilter(false);
    setDownvoteFilter(false);
  };

  if (isInitialLoading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading admin viewer...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <X className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground max-w-md">
            You don't have permission to access the admin viewer. Please log out and log back in to refresh your permissions, or contact an administrator.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Button onClick={onLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Log Out & Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-screen bg-background flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-muted px-2 py-3 flex-shrink-0">
          <div className="flex items-center justify-between px-4">
            {/* Back button */}
            <div className="flex-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>

            {/* Title centered */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Eye className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold">Admin Chat Viewer</h1>
              </div>
              <p className="text-muted-foreground text-sm">
                View and analyze all user conversations
              </p>
            </div>

            {/* Controls on the right */}
            <div className="flex gap-1 items-center flex-1 justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={onNavigateToSupport}>
                    <LifeBuoy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Support & Feedback</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={onLogout}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 px-2 pb-2 pt-0 overflow-hidden">
          <div className="h-full rounded-lg border flex flex-col">

            {/* Filters Section */}
            <div className="border-b bg-muted/50">
              <div className="px-4 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {filtersOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  {(inputMessageUuid || courseFilter || startTime || endTime || upvoteFilter || downvoteFilter) && (
                    <Badge variant="secondary" className="ml-2">Active</Badge>
                  )}
                </Button>
              </div>

              {filtersOpen && (
                <div className="px-4 pb-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Message UUID */}
                    <div className="space-y-2">
                      <Label htmlFor="messageUuid" className="text-xs font-medium">
                        Message UUID
                      </Label>
                      <Input
                        id="messageUuid"
                        placeholder="Enter message UUID..."
                        value={inputMessageUuid}
                        onChange={(e) => setInputMessageUuid(e.target.value)}
                        disabled={isLoading}
                        className="text-sm"
                      />
                    </div>

                    {/* Course Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Course</Label>
                      <Select
                        value={courseFilter || "all"}
                        onValueChange={(val) => setCourseFilter(val === "all" ? "" : val)}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="All courses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All courses</SelectItem>
                          {allCourses.map((course) => (
                            <SelectItem key={course} value={course}>
                              {course}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Start Time */}
                    <div className="space-y-2">
                      <Label htmlFor="startTime" className="text-xs font-medium">
                        Start Time
                      </Label>
                      <Input
                        id="startTime"
                        type="datetime-local"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        disabled={isLoading}
                        className="text-sm"
                      />
                    </div>

                    {/* End Time */}
                    <div className="space-y-2">
                      <Label htmlFor="endTime" className="text-xs font-medium">
                        End Time
                      </Label>
                      <Input
                        id="endTime"
                        type="datetime-local"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        disabled={isLoading}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  {/* Feedback Filters and Action Buttons */}
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="upvote"
                        checked={upvoteFilter}
                        onCheckedChange={setUpvoteFilter}
                        disabled={isLoading}
                      />
                      <Label
                        htmlFor="upvote"
                        className="text-sm font-medium flex items-center gap-1 cursor-pointer"
                      >
                        <ThumbsUp className="h-4 w-4 text-green-500" />
                        Upvoted
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="downvote"
                        checked={downvoteFilter}
                        onCheckedChange={setDownvoteFilter}
                        disabled={isLoading}
                      />
                      <Label
                        htmlFor="downvote"
                        className="text-sm font-medium flex items-center gap-1 cursor-pointer"
                      >
                        <ThumbsDown className="h-4 w-4 text-red-500" />
                        Downvoted
                      </Label>
                    </div>

                    <div className="flex-1" />

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearFilters}
                        disabled={isLoading}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear
                      </Button>
                      <Button
                        onClick={fetchConversations}
                        disabled={isLoading}
                        size="sm"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Search className="h-4 w-4 mr-2" />
                            Search
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Bar */}
            <div className="px-4 py-2 border-b bg-background flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {filteredConversations.length > 0 ? (
                    <>
                      Showing <span className="font-semibold text-foreground">{currentIndex + 1}</span> of{" "}
                      <span className="font-semibold text-foreground">{filteredConversations.length}</span> conversations
                    </>
                  ) : (
                    "No conversations found"
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevious}
                  disabled={currentIndex <= 0 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNext}
                  disabled={currentIndex < 0 || currentIndex >= filteredConversations.length - 1 || isLoading}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Conversation Info Bar */}
            {currentConversation && (
              <div className="px-4 py-2 border-b bg-muted/30">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Course:</span>
                    <Badge variant="secondary">{currentConversation.conversation_course}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Conversation ID:</span>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {currentConversation.conversation_id}
                    </code>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleCopyMessage(currentConversation.conversation_id)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Copy ID</TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Query UUID:</span>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {currentQueryUuid}
                    </code>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleCopyMessage(currentQueryUuid)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Copy UUID</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}

            {/* Chat Messages Area */}
            <ScrollArea className="flex-1 bg-background">
              <div className="max-w-4xl mx-auto p-4 space-y-4">
                {!currentConversation ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium">No conversations match your filters</p>
                    <p className="text-sm">Try adjusting your search criteria</p>
                  </div>
                ) : currentMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium">No messages in this conversation</p>
                  </div>
                ) : (
                  currentMessages.map((message, index) => (
                    <Card
                      key={message.query_uuid || index}
                      className={`border-2 ${
                        message.query_uuid === currentQueryUuid 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border'
                      }`}
                    >
                      <div className="p-4 space-y-4">
                        {/* User Question */}
                        <div className="flex justify-end">
                          <div className="max-w-[85%]">
                            <div className="flex items-center gap-2 mb-1 justify-end">
                              <span className="text-xs text-muted-foreground">User</span>
                              {message.timestamp && (
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(message.timestamp, {includeTime: true})}
                                </span>
                              )}
                            </div>
                            <div className="p-3 rounded-md bg-primary text-primary-foreground">
                              <p className="text-sm whitespace-pre-wrap">{message.query}</p>
                            </div>
                            <div className="flex items-center gap-1 mt-1 justify-end">
                              <code className="text-[10px] text-muted-foreground bg-muted px-1 rounded">
                                {message.query_uuid}
                              </code>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => handleCopyMessage(message.query_uuid)}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Copy UUID</TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        </div>

                        {/* AI Response */}
                        {message.response && (
                          <div className="flex justify-start">
                            <div className="max-w-[85%]">
                              <div className="flex items-center gap-2 mb-1">
                                <img
                                  src="/study-buddy.svg"
                                  alt="Study Buddy"
                                  className="h-4 w-4"
                                />
                                <span className="text-xs text-muted-foreground">Study Buddy</span>
                                {message.interaction_type && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                                    {message.interaction_type}
                                  </Badge>
                                )}
                              </div>
                              <div className="p-3 rounded-md bg-muted">
                                <SourceProvider>
                                  {message.interaction_type === 'search' ? (
                                    <SearchResponse message={message} />
                                  ) : (
                                    <ChatResponse message={message} />
                                  )}
                                </SourceProvider>
                              </div>

                              {/* Feedback indicators */}
                              <div className="flex flex-col gap-2 mt-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {message.vote === "up" && (
                                    <Badge variant="secondary" className="text-green-600 bg-green-100 dark:bg-green-900/30">
                                      <ThumbsUp className="h-3 w-3 mr-1" />
                                      Upvoted
                                    </Badge>
                                  )}
                                  {message.vote === "down" && (
                                    <Badge variant="secondary" className="text-red-600 bg-red-100 dark:bg-red-900/30">
                                      <ThumbsDown className="h-3 w-3 mr-1" />
                                      Downvoted
                                    </Badge>
                                  )}
                                  {message.reason && (
                                    <Badge variant="outline" className="text-muted-foreground">
                                      Reason: {message.reason}
                                    </Badge>
                                  )}
                                </div>
                                {message.feedback && (
                                  <div className="text-sm bg-muted/50 p-2 rounded border">
                                    <span className="font-medium text-muted-foreground">User Feedback: </span>
                                    <span className="text-foreground">{message.feedback}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AdminChatViewer;

